import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { creditRange, fmtCredits, PreprocessConfig, PreprocessState, setMaterialPreprocess } from '../../services/knowledgeApi';
import { isProcessing } from './PreprocessChip';

export const NOTES_PLACEHOLDER =
  'e.g. "Audience is patients. Keep dosing tables together. Start each snippet with the drug name and country. SKU means product code."';

interface Props {
  material: { id: string; name: string; vectorize_enabled?: boolean; category_id?: string | null; vectorized_at?: string | null };
  state: PreprocessState;
  config: PreprocessConfig | null;
  onClose: () => void;
  onDone: (message: string) => void;
  onViewOriginal: () => void;
}

/** Turn AI organizing on or off for one file, edit its notes, or re-run it. */
export const OrganizeDialog: React.FC<Props> = ({ material, state, config, onClose, onDone, onViewOriginal }) => {
  const { getIdToken } = useAuth();
  const [enable, setEnable] = useState<boolean>(state.method === 'ai' || !!state.requested);
  const [notes, setNotes] = useState(state.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const live = !!material.vectorize_enabled && !!material.category_id;
  const busy = isProcessing(state);
  const available = !!config?.enabled;
  const maxNotes = config?.max_notes_chars ?? 1000;
  const estimate = config && state.chars ? creditRange(state.chars, config) : null;
  const tooBig = !!(config && state.chars && state.chars > config.max_chars_per_file);

  let action = 'Save';
  if (live) {
    if (enable) action = state.method === 'ai' ? 'Re-run AI organizing' : 'AI-organize now';
    else action = state.method === 'ai' ? 'Switch to standard split' : 'Re-process';
  }

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await setMaterialPreprocess(getIdToken, material.id, enable, enable ? notes : undefined);
      onDone(
        res.queued
          ? enable
            ? 'AI organizing started. The table updates as it goes.'
            : 'Re-processing with the standard split.'
          : enable
            ? 'Saved. It will be AI-organized when you turn on vectorizing.'
            : 'Saved.',
      );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>AI organizing: {material.name}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            The AI rewrites the file into self-contained, titled snippets, one topic each, with page references, so searches find
            the right part and the answer makes sense on its own. It keeps every fact and adds nothing; parts it gets wrong use
            the standard split instead. The original file and its text are kept either way.
          </Typography>
          {busy && <Alert severity="info">This file is being processed right now. You can change settings when it finishes.</Alert>}
          {state.status === 'failed' && state.reason && <Alert severity="error">{state.reason}</Alert>}
          {state.status !== 'failed' && state.reason && (state.ai === 'partial' || state.ai === 'skipped' || state.ai === 'failed') && (
            <Alert severity="warning">{state.reason}</Alert>
          )}
          {state.method === 'ai' && state.units != null && (
            <Typography variant="body2">
              Last run: {state.units.toLocaleString()} snippets
              {state.parts ? ` from ${state.parts} part${state.parts === 1 ? '' : 's'}` : ''}
              {state.credits != null ? `, ${state.credits.toFixed(2)} credits` : ''}
              {state.estimate != null ? ` (estimated about ${fmtCredits(state.estimate)})` : ''}
              {state.calls && state.parts && state.calls > state.parts
                ? `. ${state.calls} AI calls: ${state.calls - state.parts} part${state.calls - state.parts === 1 ? '' : 's'} needed a second try`
                : ''}
              .
            </Typography>
          )}
          <FormControlLabel
            control={<Checkbox checked={enable} onChange={(e) => setEnable(e.target.checked)} disabled={busy || (!available && !enable)} />}
            label="AI-organize this file"
          />
          {!available && <Alert severity="info">AI organizing is turned off by the platform administrators right now.</Alert>}
          {enable && (
            <TextField
              label="Notes for the AI (optional)"
              multiline
              minRows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, maxNotes))}
              placeholder={NOTES_PLACEHOLDER}
              helperText={`What matters, who it's for, how to group things, terms to keep. It can't add facts or remove content. ${notes.length}/${maxNotes}`}
              disabled={busy}
            />
          )}
          {enable && live && (
            <Typography variant="caption" color="text.secondary">
              {tooBig
                ? `This file has more text than AI organizing handles (${config!.max_chars_per_file.toLocaleString()} characters), so it would use the standard split.`
                : estimate != null
                  ? `Uses your AI credits: about ${fmtCredits(estimate[0])} credits for this file, up to ${fmtCredits(estimate[1])} if parts need a second try (never more than ${config!.max_credits_per_file}).`
                  : config
                    ? `Uses your AI credits: about ${fmtCredits(config.credits_per_100k_chars)} credits per 100,000 characters (~40 pages), never more than ${config.max_credits_per_file} per file.`
                    : null}
            </Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {material.vectorized_at && <Button onClick={onViewOriginal}>View original text</Button>}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving || busy || (enable && !available)}>
          {saving ? 'Saving…' : action}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
